import React, { useMemo } from "react";
import { useParams } from "react-router";
import { Button } from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";
import { GetApp } from "@mui/icons-material";
import { useAssetTypeIdentifiers, useAuditSummaryData } from "../../hooks";
import { enums } from "../../../common/lib";
import { strings } from "../../i18n";
import { csv } from "../../lib";
import styles from "./styles";
import { logToSentry } from "../../../config/Sentry";
import { useSelector, useDispatch } from "react-redux";
import actions from "../../../Store/actions";

const columns = [
    { id: "Make", order: 4 },
    { id: "Type", order: 5 },
    { id: "Assigned", order: 7 },
    { id: "Asset Status", order: 8 },
    { id: "Action", order: 9 },
    { id: "Review Status", order: 10 },
    { id: "Auditor Exclusion Reason", order: 11 },
    { id: "Auditor Notes", order: 12 },
    { id: "Controller Exclusion Reason", order: 13 },
    { id: "Controller Notes", order: 14 }
];

type Props = {
    disabled: boolean
};

/**
 * Audit Summary Download Button Component
 */
const AuditSummaryDownloadButton = ({ disabled = false }: Props) => {
    const { userOrganisations } = useSelector((state) => state);
    const dispatch = useDispatch();

    const rawCols = Object.values(useAssetTypeIdentifiers(1));
    const identifiersCols = useMemo(() => {
        return rawCols.map((value, index) => ({
            id: value
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" "),
            order: index + 1
        }));
    }, [rawCols]);

    const currentOrg = userOrganisations?.item?.id;

    const additionalCols =
        enums.organisationIds.dilwariGroup === currentOrg
            ? [
                  { id: "Year", order: 15 },
                  { id: "Model", order: 6 }
              ]
            : [];
    const updatedCols = [...identifiersCols, ...columns, ...additionalCols];
    const { csvData } = useAuditSummaryData("csv");

    const params = useParams();

    const onDownload = () => {
        try {
            csv.download(
                updatedCols.sort((a, b) => a.order - b.order),
                csvData,
                `audit-${params.auditId}-summary`
            );
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On download summary:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onDownloadSummary"))
            );
        }
    };

    return (
        <Button
            variant="outlined"
            disabled={!csvData.length || disabled}
            fullWidth
            onClick={onDownload}
            sx={styles.downloadButtonStyles}
            size="small"
            endIcon={<GetApp />}
        >
            {strings("downloadCsv")}
        </Button>
    );
};

export default AuditSummaryDownloadButton;
